import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Divider,
  Chip,
  LinearProgress,
  IconButton,
  Link
} from '@mui/material';
import {
  Close as CloseIcon,
  Payment as PaymentIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Cancel as CancelIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import QRCode from 'qrcode';
import {
  createPaymentOrder,
  getPaymentStatus,
  cancelPaymentOrder,
  clearPaymentOrder
} from '../../store/actions/orderAction';
import { getUserInfo } from '../../store/actions/authActions';
import { useNotification } from '../Common/useNotification';
import logger from '../../utils/logger';

const PaymentModal = ({ open, onClose, selectedSku }) => {
  const dispatch = useDispatch();
  const notify = useNotification();
  const { paymentOrder } = useSelector((state) => state.order);
  const { user } = useSelector((state) => state.auth);

  // 本地状态
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState('');
  const [remainingTime, setRemainingTime] = useState(0);
  const [progressPercent, setProgressPercent] = useState(0); // 进度百分比
  const [qrRetryCount, setQrRetryCount] = useState(0); // 二维码重试次数
  const [actualTotalSeconds, setActualTotalSeconds] = useState(0); // 实际总时长（秒）
  const [pollingInterval, setPollingInterval] = useState(null);
  const [countdownInterval, setCountdownInterval] = useState(null);
  const [paymentCompleted, setPaymentCompleted] = useState(false);
  const [isCreatingOrder, setIsCreatingOrder] = useState(false); // 本地loading状态
  const [createOrderError, setCreateOrderError] = useState(null); // 创建订单错误信息

  // 引用
  const pollingRef = useRef(null);
  const countdownRef = useRef(null);
  const cancelRetryRef = useRef(false); // 取消重试标志

  // 生成二维码
  const generateQRCode = async (url) => {
    try {
      const qrCodeUrl = await QRCode.toDataURL(url, {
        width: 200,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        }
      });
      setQrCodeDataUrl(qrCodeUrl);
    } catch (error) {
      //console.error('生成二维码失败:', error);
      notify.error('生成二维码失败');
    }
  };

  // 开始支付状态轮询 - 动态间隔版本
  const startPolling = (orderId) => {
    // 清除之前的轮询
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
    }

    let pollCount = 0; // 轮询次数计数器
    const startTime = Date.now(); // 轮询开始时间

    // 动态计算轮询间隔
    const getDynamicInterval = () => {
      const elapsed = Date.now() - startTime; // 已过去的时间（毫秒）
      
      if (elapsed < 30 * 1000) {
        // 前30秒：每2秒查询一次（用户刚扫码，支付概率最高）
        return 2000;
      } else if (elapsed < 120 * 1000) {
        // 30-120秒：每3秒查询一次（保持响应性）
        return 3000;
      } else {
        // 120秒后：每5秒查询一次（节省资源）
        return 5000;
      }
    };

    const pollPaymentStatus = async () => {
      try {
        pollCount++;
        const currentInterval = getDynamicInterval();
        const elapsed = Math.floor((Date.now() - startTime) / 1000);
        
        //console.log(`支付状态轮询 #${pollCount}，间隔: ${currentInterval}ms，已过去: ${elapsed}s`);
        
        const statusData = await dispatch(getPaymentStatus(orderId));
        
        //console.log('支付状态查询结果:', statusData);

        // 检查支付状态
        if (statusData.payment_status === 'success') {
          // 支付成功
          setPaymentCompleted(true);
          stopPolling();
          stopCountdown();
          
          // 显示成功提示
          notify.success(`充值成功！获得 ${statusData.points_charged} 点数`);
          
          // 刷新用户信息
          dispatch(getUserInfo());
          
          // 延迟关闭弹窗
          setTimeout(() => {
            handleClose();
          }, 2000);
          
        } else if (statusData.payment_status === 'failed') {
          // 支付失败
          stopPolling();
          stopCountdown();
          
          if (statusData.order_status === 'cancelled') {
            notify.warning('充值已取消');
          } else if (statusData.order_status === 'expired') {
            notify.error('订单已过期，请重新创建订单');
          } else {
            notify.error('充值失败，请重试');
          }
        }
        
        // 更新剩余时间
        if (statusData.remaining_time !== undefined) {
          setRemainingTime(statusData.remaining_time);
          
          // 如果剩余时间为0，停止轮询
          if (statusData.remaining_time <= 0) {
            stopPolling();
            stopCountdown();
            handleOrderExpired();
            return; // 提前返回，不再设置下次轮询
          }
        }
        
        // 设置下次轮询（动态间隔）
        const nextInterval = getDynamicInterval();
        pollingRef.current = setTimeout(pollPaymentStatus, nextInterval);
        
      } catch (error) {
        //console.error('查询支付状态失败:', error);
        
        // 错误时使用当前间隔重试
        const retryInterval = getDynamicInterval();
        pollingRef.current = setTimeout(pollPaymentStatus, retryInterval);
      }
    };

    // 立即开始第一次轮询
    pollPaymentStatus();

    setPollingInterval(pollingRef.current);
  };

  // 停止轮询
  const stopPolling = () => {
    if (pollingRef.current) {
      clearTimeout(pollingRef.current);
      pollingRef.current = null;
      setPollingInterval(null);
    }
  };

  // 开始倒计时和进度条更新
  const startCountdown = (expiresAt) => {
    // 清除之前的倒计时
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
    }

    // 计算并记录实际总时长
    const startTime = new Date().getTime();
    const expireTime = new Date(expiresAt).getTime();
    const initialTotalSeconds = Math.max(0, Math.floor((expireTime - startTime) / 1000));
    // 进度条总时长：在实际总时长基础上减去1分钟（60秒）
    const progressTotalSeconds = Math.max(60, initialTotalSeconds - 60);
    setActualTotalSeconds(progressTotalSeconds);
    
    //console.log('订单创建成功，实际剩余时间:', initialTotalSeconds, '秒，进度条总时长:', progressTotalSeconds, '秒');

    const updateCountdownAndProgress = () => {
      const now = new Date().getTime();
      const expireTime = new Date(expiresAt).getTime();
      
      // 计算剩余时间（秒）
      const timeLeftSeconds = Math.max(0, Math.floor((expireTime - now) / 1000));
      const timeLeftMinutes = Math.floor(timeLeftSeconds / 60);
      setRemainingTime(timeLeftMinutes);
      
      // 计算进度百分比：基于开始时间计算已过去的时间
      if (progressTotalSeconds > 0) {
        const elapsedSeconds = Math.floor((now - startTime) / 1000);
        const progress = Math.min(100, Math.max(0, (elapsedSeconds / progressTotalSeconds) * 100));
        setProgressPercent(Math.round(progress * 10) / 10); // 保留一位小数，更平滑
        /*console.log('进度更新:', { 
          timeLeftSeconds, 
          elapsedSeconds,
          progressTotalSeconds, 
          progress: Math.round(progress * 10) / 10 
        });*/
      }
      
      if (timeLeftSeconds <= 0) {
        stopCountdown();
        stopPolling();
        handleOrderExpired();
      }
    };

    // 立即执行一次
    updateCountdownAndProgress();

    // 每秒更新一次（让进度条更流畅）
    countdownRef.current = setInterval(updateCountdownAndProgress, 1000);
    setCountdownInterval(countdownRef.current);
  };

  // 停止倒计时
  const stopCountdown = () => {
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
      setCountdownInterval(null);
    }
  };

  // 处理订单过期
  const handleOrderExpired = async () => {
    logger.warn('订单过期处理', { qrRetryCount, maxRetries: 3 });
    
    if (qrRetryCount >= 2) {
      // 第三次过期，不再重试
      notify.error('订单已过期');
      logger.warn('已达最大重试次数，停止重试');
      return;
    }
    
    // 第一、二次过期，重新创建订单
    const nextRetryCount = qrRetryCount + 1;
    setQrRetryCount(nextRetryCount);
    notify.warning('二维码已过期，重新创建订单');
    logger.info(`第${nextRetryCount}次重新创建订单`);
    
    // 重新创建订单
    await handleCreateOrder();
  };

  // 获取错误类型和最终用户提示
  const getErrorMessage = (error) => {
    const status = error.response?.status;
    const message = error.message || '';
    const code = error.code || '';

    // 限流错误 (429)
    if (status === 429) {
      return {
        type: 'rate_limit',
        message: '请求过于频繁，请稍后再试',
        canRetry: false
      };
    }

    // 网络错误
    const isNetworkError = 
      message.includes('Network Error') || 
      message.includes('timeout') || 
      message.includes('ERR_NETWORK') ||
      message.includes('ECONNREFUSED') ||
      message.includes('ENOTFOUND') ||
      message.includes('ECONNRESET') ||
      message.includes('ETIMEDOUT') ||
      code === 'NETWORK_ERROR' ||
      code === 'ECONNREFUSED' ||
      code === 'ENOTFOUND' ||
      code === 'ECONNRESET' ||
      code === 'ETIMEDOUT' ||
      (!status && !error.response);

    if (isNetworkError) {
      return {
        type: 'network',
        message: '网络连接异常，请稍后再试',
        canRetry: true
      };
    }

    // 服务器错误 (5xx)
    if (status >= 500) {
      return {
        type: 'server',
        message: '当前服务不可用，请稍后再试',
        canRetry: true
      };
    }

    // 其他4xx错误
    if (status >= 400 && status < 500) {
      return {
        type: 'client',
        message: '当前服务不可用，请稍后再试',
        canRetry: true
      };
    }

    // 默认错误
    return {
      type: 'unknown',
      message: '当前服务不可用，请稍后再试',
      canRetry: true
    };
  };

  // 静默重试函数
  const retryWithBackoff = async (fn, maxRetries = 3, baseDelay = 1000) => {
    let lastError;
    
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      // 检查是否需要取消重试
      if (cancelRetryRef.current) {
        logger.info('重试已被取消');
        throw new Error('重试已取消');
      }
      
      try {
        if (attempt === 0) {
          //console.log('开始创建订单...');
        } else {
          //console.log(`第${attempt}次重试开始...`);
        }
        
        return await fn();
      } catch (error) {
        lastError = error;
        
        // 如果是最后一次尝试，直接抛出错误
        if (attempt === maxRetries) {
          //console.log(`第${attempt + 1}次请求失败，已达最大重试次数，停止重试`);
          throw error;
        }
        
        // 检查是否可以重试
        const errorInfo = getErrorMessage(error);
        if (!errorInfo.canRetry) {
          //console.log(`第${attempt + 1}次请求失败，错误类型不可重试:`, errorInfo.type);
          throw error;
        }
        
        // 计算延迟时间（指数退避）
        const delay = baseDelay * Math.pow(2, attempt);
        //console.log(`第${attempt + 1}次请求失败，${delay}ms后进行第${attempt + 1}次重试...`);
        
        // 等待后重试，但要检查取消标志
        await new Promise((resolve, reject) => {
          const timeoutId = setTimeout(() => {
            if (cancelRetryRef.current) {
              reject(new Error('重试已取消'));
            } else {
              resolve();
            }
          }, delay);
          
          // 如果立即取消，清除定时器
          if (cancelRetryRef.current) {
            clearTimeout(timeoutId);
            reject(new Error('重试已取消'));
          }
        });
      }
    }
    
    throw lastError;
  };

  // 创建支付订单（简化版）
  const handleCreateOrder = async () => {
    if (!selectedSku) {
      notify.error('请选择套餐');
      return;
    }

    // 清理之前的订单状态
    dispatch(clearPaymentOrder());
    setQrCodeDataUrl('');
    setRemainingTime(0);
    setProgressPercent(0);
    setActualTotalSeconds(0); // 重置实际总时长
    
    // 重置取消标志、错误状态并开始loading状态
    cancelRetryRef.current = false;
    setCreateOrderError(null);
    setIsCreatingOrder(true);

    try {
      // 使用静默重试
      const orderData = await retryWithBackoff(
        () => dispatch(createPaymentOrder(selectedSku.sku_id, 'wechat')),
        3, // 最多重试3次
        1000 // 基础延迟1秒
      );
      
      //console.log('创建订单成功:', orderData);

      // 生成二维码
      await generateQRCode(orderData.qr_code_url);

      // 开始轮询支付状态
      startPolling(orderData.order_id);

      // 开始倒计时
      startCountdown(orderData.expires_at);

    } catch (error) {
      // 如果是取消导致的错误，不显示错误提示
      if (error.message === '重试已取消') {
        //console.log('订单创建已取消');
        return;
      }
      
      //console.error('创建订单最终失败:', error);
      
      // 获取最终错误提示
      const errorInfo = getErrorMessage(error);
      
      // 设置错误状态用于UI显示
      setCreateOrderError(errorInfo.message);
      
      // 同时显示通知
      notify.error(errorInfo.message);
    } finally {
      // 无论成功还是失败，都结束loading状态
      setIsCreatingOrder(false);
    }
  };

  // 取消订单
  const handleCancelOrder = async () => {
    if (!paymentOrder.currentOrder) return;

    try {
      await dispatch(cancelPaymentOrder(paymentOrder.currentOrder.order_id, '用户主动取消'));
      notify.info('订单已取消');
      handleClose();
    } catch (error) {
      //console.error('取消订单失败:', error);
      notify.error('取消订单失败');
    }
  };

  // 刷新订单状态
  const handleRefreshStatus = async () => {
    if (!paymentOrder.currentOrder) return;

    try {
      await dispatch(getPaymentStatus(paymentOrder.currentOrder.order_id));
    } catch (error) {
      //console.error('刷新状态失败:', error);
      notify.error('刷新状态失败');
    }
  };

  // 关闭弹窗
  const handleClose = () => {
    //console.log('关闭支付弹窗，清理所有状态和定时器');
    
    // 取消正在进行的重试
    cancelRetryRef.current = true;
    
    // 停止轮询和倒计时
    stopPolling();
    stopCountdown();
    
    // 清空支付订单状态
    dispatch(clearPaymentOrder());
    
    // 重置本地状态
    setQrCodeDataUrl('');
    setRemainingTime(0);
    setProgressPercent(0); // 重置进度百分比
    setActualTotalSeconds(0); // 重置实际总时长
    setQrRetryCount(0); // 重置重试次数
    setPaymentCompleted(false);
    setIsCreatingOrder(false); // 重置loading状态
    setCreateOrderError(null); // 重置错误状态
    
    // 调用父组件的关闭回调
    onClose();
  };

  // 弹窗打开时创建订单
  useEffect(() => {
    if (open && selectedSku && !paymentOrder.currentOrder && !isCreatingOrder && !createOrderError) {
      //console.log('弹窗打开，开始创建订单');
      handleCreateOrder();
    }
  }, [open, selectedSku]);

  // 组件卸载时清理
  useEffect(() => {
    return () => {
      //console.log('PaymentModal组件卸载，清理所有定时器');
      stopPolling();
      stopCountdown();
    };
  }, []);

  // 获取支付状态显示信息
  const getPaymentStatusInfo = () => {
    const status = paymentOrder.paymentStatus?.payment_status;
    
    switch (status) {
      case 'success':
        return {
          icon: <CheckCircleIcon sx={{ color: 'success.main', fontSize: 24 }} />,
          text: '支付成功',
          color: 'success'
        };
      case 'paying':
        return {
          icon: <PaymentIcon sx={{ color: 'primary.main', fontSize: 24 }} />,
          text: '等待支付',
          color: 'primary'
        };
      case 'failed':
        return {
          icon: <ErrorIcon sx={{ color: 'error.main', fontSize: 24 }} />,
          text: '支付失败',
          color: 'error'
        };
      default:
        return {
          icon: <PaymentIcon sx={{ color: 'primary.main', fontSize: 24 }} />,
          text: '等待支付',
          color: 'primary'
        };
    }
  };

  const statusInfo = getPaymentStatusInfo();

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          minHeight: 600
        }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PaymentIcon sx={{ color: 'primary.main' }} />
            <Typography variant="h6" component="span">
              充值套餐
            </Typography>
          </Box>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ px: 3, py: 2 }}>
        {/* 套餐信息 */}
        {selectedSku && (
          <Box sx={{ mb: 3, p: 2, backgroundColor: 'primary.main', color: 'white', borderRadius: 2 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              {selectedSku.sku_name}
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body2">
                充值 {selectedSku.sku_original_points} 点 + 赠送 {selectedSku.sku_bonus_points} 点
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                ¥{selectedSku.sku_price}
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ mt: 1, opacity: 0.9 }}>
              总共获得 {selectedSku.sku_total_points} 点数
            </Typography>
          </Box>
        )}

        {/* 创建订单加载状态 */}
        {isCreatingOrder && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CircularProgress size={60} />
            <Typography variant="body1" sx={{ mt: 2 }}>
              正在创建订单...
            </Typography>
          </Box>
        )}

        {/* 创建订单失败状态 */}
        {createOrderError && !isCreatingOrder && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <ErrorIcon sx={{ fontSize: 60, color: 'error.main', mb: 2 }} />
            <Typography variant="h6" sx={{ mb: 1, color: 'error.main' }}>
              创建订单失败
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {createOrderError}
            </Typography>
          </Box>
        )}

        {/* 支付界面 */}
        {paymentOrder.currentOrder && !paymentCompleted && (
          <Box>
            {/* 支付状态 */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
              {statusInfo.icon}
              <Typography variant="h6" sx={{ ml: 1, color: `${statusInfo.color}.main` }}>
                {statusInfo.text}
              </Typography>
            </Box>

            {/* 剩余时间显示 */}
            {remainingTime > 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mb: 2 }}>
                订单剩余时间: {remainingTime} 分钟
              </Typography>
            )}

            {/* 进度条 */}
            {remainingTime > 0 && (
              <Box sx={{ mb: 2 }}>
                <LinearProgress 
                  variant="determinate" 
                  value={progressPercent} 
                  sx={{ 
                    height: 8, 
                    borderRadius: 4,
                    backgroundColor: 'grey.200',
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 4,
                      backgroundColor: 'primary.main'
                    }
                  }}
                />
              </Box>
            )}

            {/* 二维码 */}
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              {qrCodeDataUrl ? (
                <Box>
                  <img 
                    src={qrCodeDataUrl} 
                    alt="支付二维码" 
                    style={{ 
                      width: 200, 
                      height: 200, 
                      border: '1px solid #e0e0e0',
                      borderRadius: 8
                    }} 
                  />
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    请使用微信扫描二维码完成支付
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ width: 200, height: 200, mx: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #e0e0e0', borderRadius: 2 }}>
                  <CircularProgress />
                </Box>
              )}
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* 支付说明 */}
            <Box sx={{ textAlign: 'left' }}>
              <Typography variant="body2" color="text.secondary">
                • <Link href="/payment-refund" target="_blank" sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>重要：充值与退款说明</Link>
              </Typography>
              <Typography variant="body2" color="text.secondary">
                •  点数包为虚拟商品，一经购买/使用，不支持退款。请确认后购买。
              </Typography>
              <Typography variant="body2" color="text.secondary">
                • 支付完成后点数将自动充值到您的账户
              </Typography>
              <Typography variant="body2" color="text.secondary">
                • 如遇问题请联系客服
              </Typography>
            </Box>
          </Box>
        )}

        {/* 支付成功状态 */}
        {paymentCompleted && paymentOrder.paymentStatus?.payment_status === 'success' && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CheckCircleIcon sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
            <Typography variant="h5" sx={{ mb: 1, color: 'success.main' }}>
              充值成功！
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              您已成功充值 {paymentOrder.paymentStatus.points_charged} 点数
            </Typography>
            <Chip 
              label={`当前余额: ${user?.points || 0} 点`} 
              color="success" 
              variant="outlined"
            />
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        {!paymentCompleted && paymentOrder.currentOrder && (
          <Button
            onClick={handleCancelOrder}
            disabled={paymentOrder.cancelling}
            startIcon={paymentOrder.cancelling ? <CircularProgress size={16} /> : <CancelIcon />}
          >
            取消订单
          </Button>
        )}
        <Button onClick={handleClose} variant="contained">
          {paymentCompleted ? '完成' : '关闭'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PaymentModal;